// src/pages/misc/public/SociosPage.tsx
// [HANDOFF] SOCIOS / PARTNER PROGRAM (/socios). Rebuilt from the design file ventaje-socios.html out of the landing's own pieces, so it
//   looks like part of the same site. Sections, top to bottom:
//     1 hero · 2 #comisiones (three ways to earn) · 3 "El 50%, explicado" · 4 #calificado (what a qualified lead is)
//     5 "Paso a paso" + "Las reglas claras" · 6 #registro (the 4-view form) · footer
//   Frame: the landing's top bar (logo -> /landing, gold "Quiero ser socio" -> #registro, burger -> LandingMenu), the left progress line,
//   the thin right scrollbar, the vignette and the drifting constellation lines. All of it comes from ../landing/useLandingChrome.ts.
//   Background: none here. The animated ClassicLens is mounted once in App.tsx behind everything.
// [SPACING] Every heading, card, band, button and field is a landing class (landing.css). Only pieces the landing does not have live in
//   socios.css (prefix so-), scaled to the landing's proportions. Do not copy px values from the design file.
// [URL-RENAME] /socios -> /partners. Anchors #comisiones -> #commissions, #calificado -> #qualified, #registro -> #signup.
//   [VERIFY] ContactPage.tsx still understands /contacto?tema=socio (the old page linked there); nothing on this page uses it now.
// [I18N] Texts are typed Spanish.
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import LandingMenu from '../landing/LandingMenu';
import { useLandingChrome } from '../landing/useLandingChrome';
import {
  PARTNER_GROUP_URL,
  partnerLeadLink,
  partnerLeadMessage,
  type PartnerInfo,
  type PartnerLead,
} from './wa';
import '../landing/landing.css';
import './socios.css';

/* =====================================================================
   ⚠️ BACKEND — LAS DOS LLAMADAS MÁS IMPORTANTES DE ESTA PÁGINA
   [UI-BACKEND-TODO]
   ---------------------------------------------------------------------
   Esta página hoy es sólo front end. submitPartner() y submitLead() son
   STUBS: arman el payload y no lo mandan a ningún lado. Reemplazar el
   cuerpo de cada una por la llamada real (edge function / RPC de Supabase,
   o el endpoint que se decida).

   Por qué importan tanto:
   1. submitPartner() crea al socio. Si falla en silencio, alguien cree que
      ya es socio y nosotros no lo tenemos registrado.
   2. submitLead() es la PRUEBA DE ATRIBUCIÓN. La regla del programa es
      "gana quien presentó primero, un pago por empresa". Sólo se puede
      cumplir si cada lead se guarda con timestamp del SERVIDOR (no del
      navegador), el id del socio y la empresa normalizada.
   3. Consentimiento: el checkbox "esta persona sabe que Ventaje la va a
      contactar" y la aceptación de términos se guardan con su timestamp.
      Es la evidencia de que el contacto aceptó ser contactado (LFPDPPP) y
      de que el socio aceptó los términos del programa.
   4. Pagos: de estos registros salen los $150 / $300 y el 50% mensual.
      Si no están en la base de datos, no hay forma de pagarle bien a nadie.

   Requisitos mínimos del backend:
   - Validar TODO otra vez en el servidor (los checks de abajo son UX, no seguridad).
   - Normalizar teléfonos a E.164 (+52…) antes de guardar y de buscar duplicados.
   - Detectar si la empresa ya existe (cliente, prospecto o lead de otro socio)
     y guardar el lead de todos modos, marcado como duplicado. No rechazarlo
     en silencio: al socio hay que poder decirle por qué no califica.
   - Rate limit + anti-spam (honeypot o captcha): es un formulario público.
   - Devolver errores reales: si falla, el front NO debe mostrar "listo".
   - Avisar al equipo (WhatsApp / correo / alerta interna) en cada lead nuevo.
   ===================================================================== */

interface PartnerPayload {
  name: string;
  whatsapp: string;
  email: string;
  city: string | null;
  partner_type: string;
  company: string | null;
  terms_accepted: true;
  terms_version: string;
  source_page: 'socios';
  website: string; // the honeypot (WU-140 A5): sent always, empty from a person
}

interface LeadPayload extends PartnerLead {
  partner_id: string | null;
  contact_consent: true;
  source_page: 'socios';
  website: string; // the honeypot (WU-140 A5): sent always, empty from a person
}

/* The wire (WU-143 UNIT 2): from this unit on, submitPartner() and submitLead() below are
   real - one POST each to the site's proxy api/submit-partner-form.js, which presents the
   server's secret and forwards the visitor's address to the submit-partner-form edge
   function (the page contract: docs/manifest/WU-140.md SESSION 1 RECORD § THE DESIGN PART 1
   D5). The [UI-BACKEND-TODO] header above and the JSX note in the partner view describe
   the page before this unit. A non-2xx answer is thrown as the Response, as his example did. */
type FormBody = ({ form: 'partner' } & PartnerPayload) | ({ form: 'lead' } & LeadPayload);

function postForm<T>(body: FormBody): Promise<T> {
  return fetch('/api/submit-partner-form', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  }).then((r): Promise<T> => {
    if (!r.ok) throw r;
    return r.json();
  });
}

/* Paso 1 → crear socio.
   Se llama al presionar "Continuar", ANTES del paso 2, a propósito:
   si el socio abandona en el paso 2, igual queda registrado. */
function submitPartner(payload: PartnerPayload): Promise<{ partner_id: string; returning: boolean }> {
  return postForm({ form: 'partner', ...payload });
}

/* Paso 2 → registrar lead del socio.
   El backend lo liga al partner_id y lo sella con hora del servidor:
   ese timestamp decide "quién presentó primero". */
function submitLead(payload: LeadPayload): Promise<{ lead_id: string; duplicate: boolean }> {
  return postForm({ form: 'lead', ...payload });
}

// UX checks only (the server must check again).
const hasText = (v: string) => v.trim().length > 1;
const isPhone = (v: string) => v.replace(/\D/g, '').length >= 10;
const isMail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

const PARTNER_TYPES = ['Agencia de marketing', 'Consultor', 'Asesor inmobiliario', 'Otro'] as const;

/* ---------- small icons (same strokes as the design file) ---------- */
const Svg: React.FC<{ size?: number; stroke?: string; sw?: number; children: React.ReactNode }> = ({
  size = 26,
  stroke = '#A9E0F4',
  sw = 1.8,
  children,
}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    {children}
  </svg>
);
const Yes = () => (
  <Svg size={20} sw={2}>
    <path d="M5 12.5l4.5 4.5L19 7.5" />
  </Svg>
);
const No = () => (
  <Svg size={20} sw={2} stroke="#B8CCD3">
    <path d="M6 12h12" />
  </Svg>
);
const Recurring: React.FC<{ stroke?: string; size?: number }> = ({ stroke = '#fff', size = 26 }) => (
  <Svg stroke={stroke} size={size} sw={1.9}>
    <path d="M20 11a8 8 0 0 0-14.6-4.5M4 13a8 8 0 0 0 14.6 4.5" />
    <path d="M5 3v4h4M19 21v-4h-4" />
  </Svg>
);
const WaIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <Svg size={size} stroke="#fff" sw={1.9}>
    <path d="M21 11.5a8.4 8.4 0 0 1-12.6 7.3L3 20.5l1.8-5.2A8.5 8.5 0 1 1 21 11.5z" />
  </Svg>
);

/** A check / dash row: bold title, grey line under it. */
const Li: React.FC<{ ok: boolean; title: string; children: React.ReactNode }> = ({ ok, title, children }) => (
  <div className="so-li">
    {ok ? <Yes /> : <No />}
    <div>
      <b className="so-white">{title}</b>
      <br />
      <span className="so-muted">{children}</span>
    </div>
  </div>
);

/** Numbered (or arrow) row with the landing's gold number and thin top line. */
const Row: React.FC<{ n?: string; title: string; children: React.ReactNode }> = ({ n, title, children }) => (
  <div className={`so-r${n ? '' : ' plain'}`}>
    {n ? <span className="so-n">{n}</span> : null}
    <div>
      <div className="so-r-t">{title}</div>
      <p>{children}</p>
    </div>
  </div>
);

const Field: React.FC<{ id: string; label: string; err?: boolean; msg?: string; children: React.ReactNode }> = ({
  id,
  label,
  err,
  msg,
  children,
}) => (
  <div className={`vl-field${err ? ' err' : ''}`}>
    <label htmlFor={id}>{label}</label>
    {children}
    {err && msg ? <div className="so-msg">{msg}</div> : null}
  </div>
);

const GOLD_LG = 'btn-gold inline-flex items-center justify-center min-h-[64px] px-9 rounded-xl text-[16px] font-bold uppercase tracking-[0.1em]';
const GOLD = 'btn-gold inline-flex items-center justify-center gap-2.5 min-h-[54px] px-7 rounded-[10px] text-[13px] font-bold uppercase tracking-[0.1em]';

type View = 'partner' | 'group' | 'lead' | 'done';
const EMPTY_LEAD = { company: '', city: '', name: '', role: '', wa: '', when: '', note: '', website: '' };

const SociosPage: React.FC = () => {
  const { threadFillRef, thumbRef, bgDetailRef, topSolid, barHandlers } = useLandingChrome('.so-page');
  const [menuOpen, setMenuOpen] = useState(false);
  const [lang, setLang] = useState<'es' | 'en'>('es');

  const [view, setView] = useState<View>('partner');
  const [busy, setBusy] = useState(false);
  const [failMsg, setFailMsg] = useState('');
  const [errs, setErrs] = useState<Record<string, boolean>>({});

  const [p, setP] = useState({ name: '', wa: '', email: '', city: '', type: '', company: '', terms: false, website: '' });
  const [partnerId, setPartnerId] = useState<string | null>(null);
  const [partner, setPartner] = useState<PartnerInfo | null>(null);
  const [joined, setJoined] = useState(false);

  const [l, setL] = useState(EMPTY_LEAD);
  const [cita, setCita] = useState(false);
  const [consent, setConsent] = useState(false);
  const [waUrl, setWaUrl] = useState('#');
  const [doneText, setDoneText] = useState('');

  useEffect(() => {
    const prev = document.title;
    document.title = 'Programa de socios · Ventaje';
    return () => {
      document.title = prev;
    };
  }, []);

  // Leaves room for the fixed top bar (72px), like the design's scroll-padding-top.
  const go = useCallback((selector: string) => {
    setMenuOpen(false);
    const el = document.querySelector(selector);
    if (!el) return;
    window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 72, behavior: 'smooth' });
  }, []);

  const show = (v: View) => {
    setView(v);
    setFailMsg('');
    setErrs({});
    go('#registro');
  };

  const clearErr = (key: string) => setErrs((e) => (e[key] ? { ...e, [key]: false } : e));
  const setPField = (key: keyof typeof p, value: string | boolean) => {
    setP((s) => ({ ...s, [key]: value }));
    clearErr(`p-${key}`);
  };
  const setLField = (key: keyof typeof l, value: string) => {
    setL((s) => ({ ...s, [key]: value }));
    clearErr(`l-${key}`);
  };

  const onContinue = () => {
    const next = {
      'p-name': !hasText(p.name),
      'p-wa': !isPhone(p.wa),
      'p-email': !isMail(p.email),
      'p-type': !p.type,
      'p-terms': !p.terms,
    };
    setErrs(next);
    if (Object.values(next).some(Boolean)) return;

    /* Payload del socio. Nombres de campos sugeridos; ajustar al esquema real. */
    const payload: PartnerPayload = {
      name: p.name.trim(),
      whatsapp: p.wa.trim(), // backend: normalizar a E.164
      email: p.email.trim().toLowerCase(),
      city: p.city.trim() || null,
      partner_type: p.type, // agencia / consultor / asesor / otro
      company: p.company.trim() || null,
      terms_accepted: true, // backend: guardar con timestamp del servidor
      terms_version: 'socios-v1', // TODO: versión real de los términos publicados
      source_page: 'socios',
      website: p.website, // the honeypot (WU-140 A5): sent always, empty from a person
    };
    setBusy(true);
    setFailMsg('');
    // ⚠️ BACKEND: llamada real aquí. No avanzar al paso 2 si falla.
    submitPartner(payload)
      .then((res) => {
        setPartnerId(res.partner_id);
        setPartner({ name: payload.name, whatsapp: payload.whatsapp });
        // S-5: a partner the server already knows (number and e-mail) continues to the lead step as that partner.
        show(res.returning ? 'lead' : 'group');
      })
      .catch(() => setFailMsg('No pudimos registrarte. Intenta de nuevo.'))
      .finally(() => setBusy(false));
  };

  const onSendLead = () => {
    const next = {
      'l-company': !hasText(l.company),
      'l-name': !hasText(l.name),
      'l-wa': !isPhone(l.wa),
      'l-consent': !consent,
    };
    setErrs(next);
    if (Object.values(next).some(Boolean)) return;

    /* Payload del lead. Es la prueba de atribución: guardar completo. */
    const payload: LeadPayload = {
      partner_id: partnerId, // de submitPartner()
      company: l.company.trim(), // backend: normalizar para detectar duplicados
      city: l.city.trim() || null,
      contact_name: l.name.trim(),
      contact_role: l.role.trim() || null,
      contact_whatsapp: l.wa.trim(), // backend: normalizar a E.164
      referral_type: cita ? 'appointment' : 'lead', // appointment = $300 · lead = $150
      appointment_when: cita ? l.when.trim() || null : null, // texto libre por ahora
      how_known: l.note.trim() || null,
      contact_consent: true, // ⚠️ guardar con timestamp del servidor (LFPDPPP)
      source_page: 'socios',
      website: l.website, // the honeypot (WU-140 A5): sent always, empty from a person
      // NO mandar timestamp desde el navegador: el que cuenta es el del servidor.
    };

    /* WhatsApp a Michael con el lead ya escrito.
       Se abre AQUÍ, dentro del clic y antes de cualquier promesa: si se abre
       después de una llamada asíncrona, el navegador lo bloquea como pop-up.
       ⚠️ Esto NO sustituye a submitLead(): WhatsApp es el aviso para Michael;
       el registro con timestamp del servidor sigue siendo la prueba de atribución.
       [UI-BACKEND-TODO] Hoy es wa.me (se abre en el navegador del socio). Pasará a la API de WhatsApp: el servidor manda este mismo
       mensaje después de guardar el lead, y este window.open y el botón "¿No se abrió WhatsApp?" se quitan. Ver wa.ts. */
    const url = partnerLeadLink(partnerLeadMessage(payload, partner ?? { name: '(sin datos)', whatsapp: '—' }));
    setWaUrl(url);
    window.open(url, '_blank', 'noopener');

    setBusy(true);
    setFailMsg('');
    // ⚠️ BACKEND: llamada real aquí. Sólo mostrar "Recibimos tu lead" si el servidor confirma.
    submitLead(payload)
      .then((res) => {
        // S-4: a company already registered is told with tact - the lead stays saved and pays nothing.
        setDoneText(
          res.duplicate
            ? `${payload.company} ya estaba registrada con nosotros, así que este lead no genera pago. Igual queda guardado a tu nombre.`
            : `Si ${payload.company} resulta calificado, ganas $${cita ? '300' : '150'}. Si firma, el 50% cada mes.`
        );
        show('done');
      })
      .catch(() => setFailMsg('No pudimos enviar el lead. Intenta de nuevo.'))
      .finally(() => setBusy(false));
  };

  const firstName = partner?.name.split(' ')[0] ?? '';

  return (
    <div className="ventaje-landing so-page font-montserrat" lang="es-MX">
      <div className="vl-bg-vignette" aria-hidden />
      <svg className="vl-bg-detail" ref={bgDetailRef} aria-hidden>
        <g stroke="rgba(255,255,255,0.06)" strokeWidth="1">
          <line x1="-100" y1="420" x2="1700" y2="-180" />
          <line x1="-100" y1="780" x2="1700" y2="140" />
          <line x1="-100" y1="1140" x2="1700" y2="480" />
        </g>
        <g stroke="rgba(255,255,255,0.10)" strokeWidth="0.7" fill="none">
          <polyline points="180,140 420,320 640,220 760,90" />
          <polyline points="980,260 1240,150 1380,430" />
          <polyline points="310,640 640,520 880,760 1120,600" />
          <polyline points="420,320 310,640" />
          <polyline points="1120,600 1380,430" />
        </g>
        <g fill="rgba(255,255,255,0.4)">
          {[
            [180, 140],
            [420, 320],
            [640, 220],
            [760, 90],
            [980, 260],
            [1240, 150],
            [1380, 430],
            [310, 640],
            [640, 520],
            [880, 760],
            [1120, 600],
          ].map(([cx, cy], i) => (
            <circle key={i} cx={cx} cy={cy} r={1.2} />
          ))}
        </g>
      </svg>

      <div className="vl-thread" aria-hidden>
        <div className="vl-thread-track" />
        <div className="vl-thread-fill" ref={threadFillRef} />
      </div>
      <div className="vl-scrollbar" aria-hidden {...barHandlers}>
        <div className="vl-scrollbar-thumb" ref={thumbRef} />
      </div>

      <header className={`vl-topbar${topSolid ? ' solid' : ''}`}>
        <Link to="/landing" className="vl-logo">
          Ventaje
        </Link>
        <div className="flex items-center gap-2.5">
          <button
            type="button"
            className="btn-gold hidden sm:inline-flex items-center justify-center min-h-[44px] px-[22px] rounded-[10px] text-xs font-bold uppercase tracking-[0.1em]"
            onClick={() => go('#registro')}
          >
            Quiero ser socio
          </button>
          <button
            type="button"
            className="vl-burger"
            aria-label="Menú"
            onClick={() => setMenuOpen(true)}
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </header>
      <LandingMenu
        open={menuOpen}
        lang={lang}
        onClose={() => setMenuOpen(false)}
        onLang={setLang}
      />

      {/* 1 · HERO */}
      <section className="so-hero">
        <div className="vl-wrap vl-reveal">
          <p className="vl-kicker">Programa de socios</p>
          <h1 className="vl-h1">
            Presenta una inmobiliaria. <b>Gana cada mes</b> mientras se quede.
          </h1>
          <p className="vl-lead">
            ¿Trabajas con inmobiliarias o las conoces? Te pagamos por cada una que nos presentes. Tú presentas; nosotros vendemos,
            configuramos y damos soporte.
          </p>
          <div className="vl-hero-cta">
            <button type="button" className={GOLD_LG} onClick={() => go('#registro')}>
              Quiero ser socio →
            </button>
            <button type="button" className="vl-btn-txt vl-btn-frost" onClick={() => go('#comisiones')}>
              Ver las comisiones ↓
            </button>
          </div>
        </div>
      </section>

      {/* 2 · LAS TRES COMISIONES */}
      {/* [PRICING] $150 (qualified lead) and $300 (appointment that happened) are one-time, one per company. The old page said $500 for
          the appointment; the new amount is $300. The 50% is monthly, see section 3. */}
      <section className="exp-landing-progressive-glass vl-glass-band vl-band-full" id="comisiones" data-water-cast="">
        <div className="so-wrap-wide">
          <div className="so-narrow vl-reveal">
            <p className="vl-kicker">Las comisiones</p>
            <h2 className="vl-h2">
              Tres formas de ganar. <b>La tercera no se acaba.</b>
            </h2>
          </div>
          <div className="so-tiers">
            <div className="vl-card so-tier vl-reveal">
              <div className="so-tier-head">
                <span className="so-n">01</span>
                <span className="so-ico">
                  <Svg>
                    <circle cx="9.5" cy="8" r="3.2" />
                    <path d="M3.5 19.5c0-3.3 2.7-6 6-6 1.5 0 2.9.6 4 1.5" />
                    <path d="M18 13v6M15 16h6" />
                  </Svg>
                </span>
              </div>
              <div className="vl-card-title">Nos pasas un lead calificado</div>
              <div className="so-amt">
                <span className="so-big">$150</span>
                <small>MXN</small>
              </div>
              <p className="so-muted" style={{ margin: 0 }}>
                Nos das el contacto de alguien dentro de una inmobiliaria que encaja con Ventaje.
              </p>
              <div className="so-when">Se paga cuando lo contactamos y confirmamos que es calificado — diga que sí o que no.</div>
            </div>
            <div className="vl-card so-tier vl-reveal">
              <div className="so-tier-head">
                <span className="so-n">02</span>
                <span className="so-ico">
                  <Svg>
                    <rect x="3.5" y="5" width="17" height="15" rx="3" />
                    <path d="M8 3v4M16 3v4M3.5 10h17" />
                    <path d="M9 14.8l2 2 4-4" />
                  </Svg>
                </span>
              </div>
              <div className="vl-card-title">Nos agendas la cita</div>
              <div className="so-amt">
                <span className="so-big">$300</span>
                <small>MXN</small>
              </div>
              <p className="so-muted" style={{ margin: 0 }}>
                Además de pasarnos el contacto, nos consigues la reunión con quien decide.
              </p>
              <div className="so-when">Se paga cuando la cita sucede y el lead resulta calificado.</div>
            </div>
            <div className="vl-card so-tier hi vl-reveal">
              <div className="so-tier-head">
                <span className="so-n">03</span>
                <span className="so-ico">
                  <Recurring />
                </span>
              </div>
              <div className="vl-card-title">La empresa firma con Ventaje</div>
              <div className="so-amt">
                <span className="so-big">50%</span>
                <small>cada mes</small>
              </div>
              <p className="so-muted" style={{ margin: 0 }}>
                De lo que paga por sus usuarios en planes Base o Elite, todos los meses, mientras siga con nosotros.
              </p>
              <div className="so-when">Se paga cada mes que el cliente paga. Sin tope y sin meta mínima.</div>
            </div>
          </div>
          <div className="so-note so-narrow vl-reveal">
            <Svg size={22}>
              <circle cx="12" cy="12" r="9" />
              <path d="M12 8v5M12 16.5v.01" />
            </Svg>
            <p className="so-muted" style={{ margin: 0 }}>
              <b className="so-white">Cómo se combinan:</b> por cada empresa ganas <b className="so-white">$150 o $300</b>, según hasta dónde
              llegues. Si esa empresa firma, <b className="so-white">además</b> empiezas a ganar el 50% mensual.
            </p>
          </div>
        </div>
      </section>

      {/* 3 · EL 50% */}
      {/* [PRICING] "Ingreso comisionable" = what the client pays for Base / Elite seats only, i.e. quote.plan in orderQuote.ts (not
          quote.total). Light seats, add-ons and AI / WhatsApp / call usage never count. Prices typed below ($299 / $599 / $99, the
          $2,990 example) must follow the real plan prices if they change.
          [VERIFY] The design says "Lite"; the app calls it "Plan Light". Pick one name. */}
      <section className="exp-landing-black-glass vl-black-band" data-water-cast="">
        <div className="vl-wrap">
          <div className="vl-reveal">
            <p className="vl-kicker">El 50%, explicado</p>
            <h2 className="vl-h-black so-h-left">
              No es un pago único. <b>Es la mitad de la suscripción, cada mes.</b>
            </h2>
            <p className="vl-lead">
              El 50% se calcula sobre el <b className="so-white">ingreso comisionable</b>: lo que el cliente paga por sus usuarios en los
              planes <b className="so-white">Base</b> o <b className="so-white">Elite</b>. El consumo se cobra aparte y no genera comisión.
            </p>
            <div className="so-pills">
              <span className="so-pill">Base · $299 / usuario</span>
              <span className="so-pill">Elite · $599 / usuario</span>
              <span className="so-pill off">Lite · $99</span>
            </div>
          </div>

          <div className="so-split">
            <div className="vl-card yes vl-reveal">
              <h3>Sí cuenta para tu 50%</h3>
              <Li ok title="Usuarios en plan Base">
                $299 por usuario al mes → <b className="so-white">$149.50</b> para ti, por usuario.
              </Li>
              <Li ok title="Usuarios en plan Elite">
                $599 por usuario al mes → <b className="so-white">$299.50</b> para ti, por usuario.
              </Li>
              <Li ok title="Cada usuario nuevo">
                Si su equipo crece, tu comisión crece con él.
              </Li>
            </div>
            <div className="vl-card vl-reveal">
              <h3>No cuenta para tu 50%</h3>
              <Li ok={false} title="El plan Lite">
                $99 por usuario. Es para equipos muy pequeños y no genera comisión.
              </Li>
              <Li ok={false} title="Consumo de IA">
                Mensajes y acciones que hace la IA.
              </Li>
              <Li ok={false} title="Mensajes de WhatsApp">
                Lo que cobra el canal por cada mensaje.
              </Li>
              <Li ok={false} title="Minutos de llamada">
                El costo de las llamadas que se hacen.
              </Li>
            </div>
          </div>

          <div className="vl-card so-example vl-reveal">
            <span className="so-ex-label">Ejemplo</span>
            <div className="vl-card-title" style={{ marginBottom: 18 }}>
              Una inmobiliaria de 10 asesores, en plan Base
            </div>
            <div className="so-ex-row">
              <span>10 usuarios × $299</span>
              <b>$2,990 / mes</b>
            </div>
            <div className="so-ex-row">
              <span className="so-muted">Consumo de IA, WhatsApp y llamadas</span>
              <span className="so-muted">aparte · sin comisión</span>
            </div>
            <div className="so-ex-total">
              <span className="vl-tag" style={{ color: 'var(--vl-gold)' }}>
                Tu 50%
              </span>
              <span className="so-big">
                $1,495<span> / mes</span>
              </span>
            </div>
            <p className="so-muted so-small" style={{ margin: '18px 0 0' }}>
              En 12 meses ese cliente te deja <b className="so-white">$17,940</b>. Si eligen Elite, son{' '}
              <b className="so-white">$2,995 al mes</b>.
            </p>
          </div>

          <div className="so-rows2 vl-reveal">
            <Row n="→" title="Mientras el cliente siga, tú cobras.">
              No es por 12 meses. Si se queda tres años, son tres años de comisión.
            </Row>
            <Row n="→" title="Te pagamos cuando el cliente nos paga.">
              Si un mes no paga, ese mes no hay comisión. Si cancela, la comisión termina.
            </Row>
            <Row n="→" title="Sin meta mínima para llegar al 50%.">
              Desde el primer cliente, no después de vender cierta cantidad.
            </Row>
            <Row n="→" title="Nosotros hacemos el trabajo pesado.">
              La configuración y la migración de sus datos corren por nuestra cuenta, gratis. Tú no das soporte técnico.
            </Row>
          </div>
        </div>
      </section>

      {/* 4 · LEAD CALIFICADO */}
      <section className="vl-open" id="calificado">
        <div className="vl-wrap">
          <div className="vl-reveal">
            <p className="vl-kicker">Antes de enviar</p>
            <h2 className="vl-h2">
              ¿Qué es un <b>lead calificado?</b>
            </h2>
            <p className="vl-lead">
              Una persona <b className="so-white">dentro de una empresa</b> que vende inmuebles, que encaja con Ventaje y que sabe que la vamos
              a contactar.
            </p>
          </div>
          <div className="so-split">
            <div className="vl-card yes vl-reveal">
              <h3>Sí califica</h3>
              <Li ok title="Es una empresa, no una persona sola.">
                Inmobiliaria, desarrolladora o master broker con equipo de ventas.
              </Li>
              <Li ok title="Vende proyectos con varias unidades.">
                Preventa de departamentos, condominios, casas en desarrollo o lotes.
              </Li>
              <Li ok title="Nos das a alguien que decide.">
                Dueño, director o gerente de ventas, con nombre y WhatsApp.
              </Li>
              <Li ok title="Sabe que lo vamos a contactar.">
                Le avisaste de nosotros y está de acuerdo en que le escribamos.
              </Li>
            </div>
            <div className="vl-card no vl-reveal">
              <h3>No califica</h3>
              <Li ok={false} title="Un asesor independiente">
                que trabaja solo o vende casas sueltas de reventa.
              </Li>
              <Li ok={false} title="Una empresa que ya tenemos">
                como cliente o en conversación. Gana quien la presentó primero.
              </Li>
              <Li ok={false} title="Un contacto sin nombre o sin WhatsApp">
                o el teléfono general de la oficina.
              </Li>
              <Li ok={false} title="Alguien que no sabe de nosotros">
                o que no quiere que lo contacten.
              </Li>
            </div>
          </div>
        </div>
      </section>

      {/* 5 · CÓMO FUNCIONA + REGLAS */}
      <section className="exp-landing-black-glass vl-black-band" data-water-cast="">
        <div className="so-wrap-wide">
          <div className="vl-reveal">
            <p className="vl-kicker">Paso a paso</p>
            <h2 className="vl-h2">
              Tú presentas. <b>Nosotros hacemos el resto.</b>
            </h2>
          </div>
          <div className="so-steps vl-reveal">
            <div className="so-step">
              <Svg>
                <rect x="5" y="3.5" width="14" height="17" rx="3" />
                <path d="M9 8h6M9 12h6M9 16h3" />
              </Svg>
              <span className="so-n">01</span>
              <div className="so-step-t">Te registras</div>
              <p>Dos minutos, aquí abajo.</p>
            </div>
            <div className="so-step">
              <Svg>
                <circle cx="9" cy="9" r="2.6" />
                <path d="M3.5 19c0-3 2.5-5.4 5.5-5.4s5.5 2.4 5.5 5.4" />
                <circle cx="16.5" cy="10" r="2.1" />
                <path d="M15 14.2c2.6.2 5 2.3 5 4.8" />
              </Svg>
              <span className="so-n">02</span>
              <div className="so-step-t">Entras al grupo de socios</div>
              <p>Te conocemos, te damos el material y resolvemos dudas.</p>
            </div>
            <div className="so-step">
              <Svg>
                <path d="M21 3 10.5 13.5M21 3l-6.5 18-4-7.5L3 9.5z" />
              </Svg>
              <span className="so-n">03</span>
              <div className="so-step-t">Nos presentas un lead</div>
              <p>Por el formulario de leads, para que quede registrado a tu nombre.</p>
            </div>
            <div className="so-step">
              <Svg>
                <rect x="3" y="6" width="18" height="12" rx="3" />
                <circle cx="12" cy="12" r="2.6" />
                <path d="M6.5 9.5v.01M17.5 14.5v.01" />
              </Svg>
              <span className="so-n">04</span>
              <div className="so-step-t">Lo llamamos y, si califica, te pagamos</div>
              <p>$150, o $300 si la cita sucedió. Compre o no.</p>
            </div>
            <div className="so-step gold">
              <Recurring stroke="#EBC05A" />
              <span className="so-n">05</span>
              <div className="so-step-t">Si firma, cobras cada mes</div>
              <p>El 50% del ingreso comisionable.</p>
            </div>
          </div>

          <p className="so-subhead vl-reveal">Las reglas claras</p>
          <div className="so-rows2 vl-reveal">
            <Row title="¿Y si otro socio presenta la misma empresa?">Gana quien la presentó primero. Un solo pago por empresa.</Row>
            <Row title="¿Cobro aunque no compre?">Sí: los $150 o $300 se pagan si califica. El 50% sólo si firma.</Row>
            <Row title="¿Quién decide si califica?">Nosotros, después de hablar con él. Si no califica, te decimos por qué.</Row>
            <Row title="¿Tengo que saber de CRM?">No. La demo, la venta y la configuración las hacemos nosotros.</Row>
            <Row title="¿Qué planes cuentan para el 50%?">Base y Elite. El plan Lite no genera comisión.</Row>
            <Row title="¿Sólo Mérida?">No, aceptamos empresas de todo México.</Row>
          </div>
        </div>
      </section>

      {/* 6 · FORMULARIO */}
      <section className="exp-landing-progressive-glass vl-glass-band vl-band-full" id="registro" data-water-cast="">
        <div className="vl-wrap">
          <div className="vl-reveal" style={{ textAlign: 'center' }}>
            <p className="vl-kicker">Regístrate</p>
            <h2 className="vl-h2">
              Hazte socio y <b>entra al grupo.</b>
            </h2>
          </div>

          {/*
            ⚠️ BACKEND — FORMULARIO DE SOCIOS. ESTO NO ES DECORATIVO.
            Hoy este formulario NO envía nada. Antes de publicar, conectar las dos llamadas
            marcadas arriba en este archivo: submitPartner() y submitLead().
            Sin ellas perdemos socios y, peor, perdemos la prueba de quién presentó a cada
            empresa primero (de eso depende a quién le pagamos).
            [UI-BACKEND-TODO] The four views are React state only (`view`). Refreshing the page starts again at step 1.
          */}
          <div className="vl-card so-form-card vl-reveal">
            {/* paso 1: socio */}
            {view === 'partner' && (
              <div>
                <div className="vl-tag" style={{ color: 'var(--vl-gold)' }}>
                  Paso 1 de 2 · Tus datos
                </div>
                <div className="so-grid2">
                  <Field id="f-nombre" label="Nombre completo" err={errs['p-name']} msg="Escribe tu nombre.">
                    <input id="f-nombre" placeholder="Laura Méndez" autoComplete="name" value={p.name} onChange={(e) => setPField('name', e.target.value)} />
                  </Field>
                  <Field id="f-wa" label="WhatsApp" err={errs['p-wa']} msg="Necesitamos tu WhatsApp para pagarte y avisarte.">
                    <input id="f-wa" inputMode="tel" placeholder="999 123 4567" autoComplete="tel" value={p.wa} onChange={(e) => setPField('wa', e.target.value)} />
                  </Field>
                  {/* The honeypot (WU-140 A5): a person never sees or fills it; the server refuses a non-empty value. */}
                  <input className="sr-only" name="website" autoComplete="off" tabIndex={-1} aria-hidden="true" value={p.website} onChange={(e) => setPField('website', e.target.value)} />
                </div>
                <div className="so-grid2">
                  <Field id="f-mail" label="Correo" err={errs['p-email']} msg="Revisa tu correo.">
                    <input id="f-mail" inputMode="email" placeholder="tu@correo.com" autoComplete="email" value={p.email} onChange={(e) => setPField('email', e.target.value)} />
                  </Field>
                  <Field id="f-ciudad" label="Ciudad">
                    <input id="f-ciudad" placeholder="Mérida" value={p.city} onChange={(e) => setPField('city', e.target.value)} />
                  </Field>
                </div>
                <div className="vl-field">
                  <label>¿Qué haces?</label>
                  <div className="vl-opts so-chips">
                    {PARTNER_TYPES.map((t) => (
                      <button key={t} type="button" className={p.type === t ? 'vl-selected' : ''} aria-pressed={p.type === t} onClick={() => setPField('type', t)}>
                        {t}
                      </button>
                    ))}
                  </div>
                  {errs['p-type'] ? <div className="so-msg">Elige una opción.</div> : null}
                </div>
                <Field id="f-empresa" label="Empresa (opcional)">
                  <input id="f-empresa" placeholder="Nombre de tu agencia o negocio" value={p.company} onChange={(e) => setPField('company', e.target.value)} />
                </Field>
                {/* [VERIFY] "términos del programa" and "aviso de privacidad" are plain text: there are no pages for them yet. */}
                <label className={`so-check${errs['p-terms'] ? ' err' : ''}`}>
                  <input type="checkbox" checked={p.terms} onChange={(e) => setPField('terms', e.target.checked)} />
                  <span className="so-box" />
                  <span>Acepto los términos del programa de socios y el aviso de privacidad.</span>
                </label>
                {failMsg ? <p className="so-msg" role="alert">{failMsg}</p> : null}
                <div className="so-form-foot">
                  <div className="so-dots">
                    <span className="on" />
                    <span />
                  </div>
                  <button type="button" className={GOLD} disabled={busy} onClick={onContinue}>
                    Continuar →
                  </button>
                </div>
              </div>
            )}

            {/* paso 2: bienvenida + grupo de socios */}
            {view === 'group' && (
              <div>
                <div className="vl-tag" style={{ color: 'var(--vl-gold)' }}>
                  Paso 2 de 2 · Bienvenida
                </div>
                <div className="vl-card-title" style={{ marginTop: 13 }}>
                  {`Bienvenido${firstName ? `, ${firstName}` : ''}. Ya eres socio.`}
                </div>
                <p className="so-muted" style={{ margin: 0 }}>
                  Esto no es sólo un formulario: trabajamos juntos. Entra al grupo de socios y te escribimos personalmente para conocerte.
                </p>
                <div className="so-rows">
                  <Row n="01" title="En el grupo">
                    Anunciamos cada empresa que firma, compartimos el material para presentar Ventaje y resolvemos dudas.
                  </Row>
                  <Row n="02" title="Te escribimos por WhatsApp">
                    Para conocerte y ver juntos a qué empresas puedes presentarnos.
                  </Row>
                  <Row n="03" title="Cuando tengas un lead">
                    Mándalo por el formulario de leads, no por el grupo: así queda registrado a tu nombre y gana quien lo presentó primero.
                  </Row>
                </div>
                {/* Grupo de socios: enlace de invitación en PARTNER_GROUP_URL (wa.ts).
                    [VERIFY] Considerar una Comunidad de WhatsApp con canal de anuncios en vez de un grupo abierto: en un grupo normal
                    todos los socios ven el número de todos.
                    TODO(backend, opcional): registrar que el socio hizo clic (group_join_clicked + timestamp) para saber a quién
                    escribirle si nunca entró. */}
                <a
                  className={`${GOLD} w-full`}
                  style={{ marginTop: 28 }}
                  href={PARTNER_GROUP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setJoined(true)}
                >
                  <WaIcon />
                  Unirme al grupo de socios
                </a>
                {joined ? (
                  <p className="so-muted" style={{ margin: '15px 0 0', fontSize: 16, textAlign: 'center' }}>
                    Te vemos en el grupo. Te escribimos pronto.
                  </p>
                ) : null}
                <div className="so-form-foot" style={{ marginTop: 25 }}>
                  <div className="so-dots">
                    <span className="on" />
                    <span className="on" />
                  </div>
                  <button type="button" className="vl-btn-txt" onClick={() => show('lead')}>
                    ¿Ya tienes un lead? Envíalo ahora →
                  </button>
                </div>
              </div>
            )}

            {/* formulario de leads (opcional, después del grupo) */}
            {view === 'lead' && (
              <div>
                <div className="vl-tag" style={{ color: 'var(--vl-gold)' }}>
                  Enviar un lead
                </div>
                <p className="so-muted" style={{ margin: '13px 0 0', fontSize: 16 }}>
                  Queda registrado a tu nombre con fecha y hora. Si alguien más presenta la misma empresa después, el lead es tuyo.
                </p>
                <div className="so-grid2">
                  <Field id="l-empresa" label="Empresa" err={errs['l-company']} msg="¿Cómo se llama la empresa?">
                    <input id="l-empresa" placeholder="Inmobiliaria Sol" value={l.company} onChange={(e) => setLField('company', e.target.value)} />
                  </Field>
                  <Field id="l-ciudad" label="Ciudad">
                    <input id="l-ciudad" placeholder="Mérida" value={l.city} onChange={(e) => setLField('city', e.target.value)} />
                  </Field>
                  {/* The honeypot (WU-140 A5): a person never sees or fills it; the server refuses a non-empty value. */}
                  <input className="sr-only" name="website" autoComplete="off" tabIndex={-1} aria-hidden="true" value={l.website} onChange={(e) => setLField('website', e.target.value)} />
                </div>
                <div className="so-grid2">
                  <Field id="l-nombre" label="Nombre del contacto" err={errs['l-name']} msg="Necesitamos un nombre.">
                    <input id="l-nombre" placeholder="Nombre y apellido" value={l.name} onChange={(e) => setLField('name', e.target.value)} />
                  </Field>
                  <Field id="l-puesto" label="Puesto">
                    <input id="l-puesto" placeholder="Director de ventas" value={l.role} onChange={(e) => setLField('role', e.target.value)} />
                  </Field>
                </div>
                <Field id="l-wa" label="WhatsApp del contacto" err={errs['l-wa']} msg="Sin WhatsApp no califica.">
                  <input id="l-wa" inputMode="tel" placeholder="999 765 4321" value={l.wa} onChange={(e) => setLField('wa', e.target.value)} />
                </Field>
                <div className="vl-field">
                  <label>¿Qué nos traes?</label>
                  <div className="vl-opts so-chips">
                    <button type="button" className={!cita ? 'vl-selected' : ''} aria-pressed={!cita} onClick={() => setCita(false)}>
                      Sólo el contacto · $150
                    </button>
                    <button type="button" className={cita ? 'vl-selected' : ''} aria-pressed={cita} onClick={() => setCita(true)}>
                      Ya agendé la cita · $300
                    </button>
                  </div>
                </div>
                {cita ? (
                  <Field id="l-fecha" label="¿Cuándo es la cita?">
                    <input id="l-fecha" placeholder="Martes 20, 11:00" value={l.when} onChange={(e) => setLField('when', e.target.value)} />
                  </Field>
                ) : null}
                <Field id="l-nota" label="¿Cómo lo conoces? (opcional)">
                  <textarea id="l-nota" placeholder="Trabajamos juntos en su campaña de Meta…" value={l.note} onChange={(e) => setLField('note', e.target.value)} />
                </Field>
                <label className={`so-check${errs['l-consent'] ? ' err' : ''}`}>
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => {
                      setConsent(e.target.checked);
                      clearErr('l-consent');
                    }}
                  />
                  <span className="so-box" />
                  <span>Esta persona sabe que Ventaje la va a contactar y está de acuerdo.</span>
                </label>
                {/* [UI-BACKEND-TODO] This line goes away when the lead is sent through the WhatsApp API instead of wa.me. */}
                <p className="so-muted so-small" style={{ margin: '18px 0 0' }}>
                  Se abre WhatsApp con el mensaje ya escrito. Sólo presiona enviar.
                </p>
                {failMsg ? <p className="so-msg" role="alert">{failMsg}</p> : null}
                <div className="so-form-foot">
                  {/* "Atrás" desde el formulario de leads: vuelve a la bienvenida. Sin llamada al backend. */}
                  <button type="button" className="vl-btn-out" onClick={() => show('group')}>
                    Atrás
                  </button>
                  <button type="button" className={GOLD} disabled={busy} onClick={onSendLead}>
                    <WaIcon size={18} />
                    Enviar por WhatsApp
                  </button>
                </div>
              </div>
            )}

            {/* listo */}
            {view === 'done' && (
              <div style={{ textAlign: 'center' }}>
                <div className="so-done-ico">
                  <Svg size={29} stroke="#fff" sw={2.4}>
                    <path d="M5 12.5l4.5 4.5L19 7.5" />
                  </Svg>
                </div>
                <div className="vl-card-title">Recibimos tu lead.</div>
                <p className="so-muted">{doneText}</p>
                <div className="so-rows" style={{ textAlign: 'left' }}>
                  <Row n="01" title="Contactamos a tu lead">
                    Le llamamos o lo vemos en persona.
                  </Row>
                  <Row n="02" title="Te avisamos por WhatsApp">
                    Si calificó, cuándo te pagamos, y si avanza hacia firmar.
                  </Row>
                </div>
                {/* [UI-BACKEND-TODO] Fallback for when the browser blocked the wa.me pop-up. Remove with the WhatsApp API switch. */}
                <a className={GOLD} style={{ marginTop: 23 }} href={waUrl} target="_blank" rel="noopener noreferrer">
                  ¿No se abrió WhatsApp? Ábrelo aquí
                </a>
                <br />
                <button
                  type="button"
                  className="vl-btn-out"
                  style={{ marginTop: 15 }}
                  onClick={() => {
                    setL(EMPTY_LEAD);
                    setConsent(false);
                    show('lead');
                  }}
                >
                  Enviar otro lead
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* [VERIFY] "Términos del programa · Aviso de privacidad" are plain text until those pages exist. */}
      <footer className="vl-footer">
        <div className="vl-foot-grid">
          <span className="vl-logo">Ventaje</span>
          <span>Programa de socios · Mérida, Yucatán · 2026</span>
          <span>Términos del programa · Aviso de privacidad</span>
        </div>
      </footer>
    </div>
  );
};

export default SociosPage;
